import { useRouter } from 'next/router';
import { useQuery } from 'react-query';
import type { CurrentProductResponse } from '@pages/api/current-product';
import { nextApiCall } from '@libs/nextApiCall';
import { nextApiCallErrorFn } from '@libs/nextApiCallErrorFn';
import * as Sentry from '@sentry/nextjs';
import { QUERY_KEYS } from '@libs/constants';

export const useProductData = (
  productId: string,
  country: string,
  currency: string,
  deliveryTimeLocale: string,
  locale: string
) => {
  const router = useRouter();
  const { data, isLoading } = useQuery<CurrentProductResponse>(
    [QUERY_KEYS.CURRENT_PRODUCT, productId, country, currency, deliveryTimeLocale, locale],
    async (): Promise<CurrentProductResponse> => {
      const response = await nextApiCall('current-product', {
        productId,
        country,
        currency,
        deliveryTimeLocale,
        locale,
      });

      if (!response.ok) {
        const { status, statusText } = response;
        const error = new Error(`${status}: ${statusText}`);
        nextApiCallErrorFn(error, `Failed to fetch product data for ${productId}`);
        return null;
      }

      return (await response.json()) as CurrentProductResponse;
    },
    {
      enabled: router.isReady,
      onError: error => {
        const errorObject = error as Record<string, string>;
        const message = errorObject?.message ? errorObject.message : JSON.stringify(error);

        Sentry.captureException(new Error(`Fetch current product data failed: ${message}`));
      },
    }
  );

  return { data, isLoading };
};
